import { applyLogic } from '@joyfill/conditional-logic';
import { joyDocHelper } from '@builttocreate/engine-utils';
import FieldTypes from '../constants/FieldTypes';
import { flattenArray } from './collectionHelper';
import { getFileViewSelector, sortFieldPositionsByXAndYCoordinates } from './documentHelper';

const { getPageOrder, sortPages } = joyDocHelper;

export const validate = (doc, options = {}) => {

  const { view } = options;
  const defaultFile = doc?.files[0];
  const validated = { status: null, fieldValidations: [] };

  if (!defaultFile) return validated;

  /**
   * Step 1: Get the target file view from the document. Defaults to 'desktop' view.
   */
  const fileView = getFileViewSelector({ doc, view });

  const fieldValidations = [];

   /**
   * Step 2: Properly sort the pages and apply conditional logic to them.
   */
  applyLogic(
    sortPages(fileView.pages, getPageOrder(fileView.pageOrder, fileView.pages)),
    doc.fields,
    '_id'
  ).forEach((page) => {

    //2.1 Apply conditional logic to the page fields
    const logicAppliedFields = applyLogic(page.fields, doc.fields, '_id');

    //2.2 Sort page fields
    const pageFields = sortFieldPositionsByXAndYCoordinates(logicAppliedFields);

    //2.3 Validate each page field
    pageFields.forEach((field) => {

      /**
       * Handle Hidden Fields:
       * 
       * 1. A hidden field is always considered valid
       * 2. A field on a hidden page is always considered valid
       */
      if (page.hidden || field.hidden) {
        fieldValidations.push({ field, pageId: page._id, status: 'valid' });
      } 

      //Handle all other visible fields
      else {

        const nextFieldValidation = validateField(field);
        nextFieldValidation.pageId = page._id;

        fieldValidations.push(nextFieldValidation);

      }


    });

  });

  /**
   * Step 3: Check if all fields are valid to determine the overall status
   */
  const allFieldsValid = fieldValidations.every((validation) => validation.status === 'valid')

  return {
    status: allFieldsValid ? 'valid' : 'invalid',
    fieldValidations
  };

};

/**
 * valid: Field is not required OR Field is hidden OR Field value is populated.
 * invalid: Field is required AND Field is visible AND Field value is empty.
 *
 * @param {Object} field
 *
 * @returns Array [{field, status: 'valid' | 'invalid'}]
 */
export function validateField(field) {

  //Handle collection fields
  if (field.type === FieldTypes.collection && isCollectionFieldEmpty(field)) {
    return { field, status: 'invalid' };
  } 

  //Handle all other fields
  else {

    if (field.required && isEmptyValue(field.value, field.type)) {
      return { field, status: 'invalid' };
    } else {
      return { field, status: 'valid' };
    }

  } 

}

/**
 * Check if the collection field is empty
 * 
 * This is a special use case for collection fields that need to be validated differently than other fields.
 * Because collection fields can have nested required tables and columns. So we can't only check the top 
 * level required field property and presence of values. We actually have to check all the nested rows 
 * as well to see if any are missing required values.
 * 
 * @param {Object} field 
 * @returns {Boolean}
 */
export function isCollectionFieldEmpty(field) {

  let emptyField = false;

  /**
   * Handle top level required field empty check
   */
  if (field.required && (!field.value || field.value?.length === 0 || field.value?.every((item) => item === ''))) {
    emptyField = true;
  }

  const flattenedArray = flattenArray(field.type, field.displayType, field.fieldPositionSchema, field.schema, field.value, field.mode);

  const invalidRowFound = flattenedArray.find((row) => row.isInvalid);
  if (invalidRowFound) emptyField = true;

  return emptyField;

}

export function isEmptyValue(value, fieldType) {
  if (
    fieldType === FieldTypes.multiSelect ||
    fieldType === FieldTypes.image ||
    fieldType === FieldTypes.table ||
    fieldType === FieldTypes.file
  ) {
    return (
      !Array.isArray(value) ||
      value.length === 0 ||
      value.every((item) => item === '')
    );
  }
  return value === '' || value === null || value === undefined;
}

export default {
  validate, 
  validateField
};
